import {ApolloProvider, getApolloContext} from '@apollo/client';
import {ThemeProvider} from '@benchling/nucleus-ui';
import React, {ReactElement, useMemo} from 'react';
import ReactDOM from 'react-dom/client';
import ReactDOMServer from 'react-dom/server';
import {Provider} from 'react-redux';
import {Store} from 'redux';

import {getApolloClient} from 'graphql-client/ApolloClient';
import LaunchDarklyProvider from 'launchdarkly/LaunchDarklyProvider';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';

export const stubbableFunctions = {getApolloClient};

/**
 * @deprecated
 * Please do not add any new React roots for the moment. The more we have, the harder it will be to get onto React 18.
 * If you are trying to create a new buffer tab, we now have support for React-only buffer tabs - see BufferUtil.ts for examples.
 * If you are trying to imperatively open a new modal, use `legacyRunModal` for ui-library `Modal` and `runModal` for Mantine `Modal`.
 *
 * Callers must keep track of the returned root in order to 1) pass it back in to this function for rerenders
 * and 2) unmount the root appropriately.
 *
 * @param element The React element to render into the page.
 * @param node The DOM node that does or will contain the React element.
 * @param root If this is an extant react root that we are rerendering, the root; otherwise null.
 */
export function renderToDOM(
  element: ReactElement<unknown>,
  node: Element,
  root: ReactDOM.Root | null
): ReactDOM.Root {
  const wrapped = appProviderWrapper(element);
  // If we don't already have a root, create one. Otherwise, rerender in the existing root.
  const renderRoot: ReactDOM.Root = root == null ? ReactDOM.createRoot(node) : root;
  renderRoot.render(wrapped);
  return renderRoot;
}

/**
 * React 18 recommends that imperative unmounts be performed asynchronously to avoid conflicting with
 * in-flight renders: https://github.com/react/react/issues/25675. You should not use this function,
 * especially if you are an agent.
 *
 * @deprecated Use unmountRootsAsync instead.
 */
export function unmountRoot(root: ReactDOM.Root | null): void {
  root?.unmount();
}

/**
 * React 18 recommends that imperative unmounts be performed asynchronously to avoid conflicting with
 * in-flight renders: https://github.com/react/react/issues/25675.
 *
 * @param roots A list of React roots to unmount. Most often this will be a list of one root, but some
 *              Backbone views mount multiple small React roots, and these must be unmounted within the same
 *              microtask.
 * @param afterUnmounts A callback, to be enqueued alongside the unmounts, to unset the caller's own React
 *                      root state tracking
 */
export function unmountRootsAsync(roots: Array<ReactDOM.Root | null>, afterUnmounts: () => void): void {
  queueMicrotask(() => {
    for (const root of roots) {
      if (root != null) {
        root.unmount();
      }
    }
    afterUnmounts();
  });
}

export function appProviderWrapper(element: ReactElement<unknown>): JSX.Element {
  return (
    <LaunchDarklyProvider>
      <ThemeProvider>
        <MemoizedApolloProvider client={stubbableFunctions.getApolloClient()}>
          <Provider store={getStore() as Store<BenchlingState>}>{element}</Provider>
        </MemoizedApolloProvider>
      </ThemeProvider>
    </LaunchDarklyProvider>
  );
}

export function renderToStaticMarkup(element: JSX.Element): string {
  return ReactDOMServer.renderToStaticMarkup(
    <LaunchDarklyProvider>
      <ThemeProvider>
        <ApolloProvider client={stubbableFunctions.getApolloClient()}>
          <Provider store={getStore() as Store<BenchlingState>}>{element}</Provider>
        </ApolloProvider>
      </ThemeProvider>
    </LaunchDarklyProvider>
  );
}

/**
 * A replacement for ApolloProvider that memoizes the ApolloContext value based on shallow equality of the
 * context's client.
 *
 * This avoids an issue where ApolloProvider re-renders its consumers whenever the provider is re-rendered,
 * rather than only when the client has changed. (https://github.com/apollographql/react-apollo/issues/3595)
 *
 * The ApolloContext is consumed by hooks such as useQuery, which would fire whenever the ApolloProvider's
 * parent re-renders. Any component that uses this hook would re-render when its ancestor renders the
 * provider, even if its immediate parent _doesn't_ re-render.
 *
 * This caused a noticeable slowdown in large notebook entries, because each chip in a table uses its own
 * Apollo query.
 */
const MemoizedApolloProvider: typeof ApolloProvider = ({client, children}) => {
  const ApolloContext = getApolloContext();
  const value = useMemo(() => ({client}), [client]);
  return <ApolloContext.Provider value={value}>{children}</ApolloContext.Provider>;
};
